// @ts-check

/** @file App-level observable projection of the active project coordinator. */

import {useSyncExternalStore} from "react"

/** @import {SyncCoordinatorStatus} from "velocious/build/src/sync/sync-coordinator-types.js" */

const STOPPED_STATUS = immutableStatus({
  conflicts: [],
  failure: null,
  lastSuccessAt: null,
  nextRetryAt: null,
  pendingCount: 0,
  rejectedCount: 0,
  state: "stopped"
})

/** Small external store consumed by authenticated chrome without gating routes. */
export class ProjectSyncStatusStore {
  /** Creates an inactive observable store. */
  constructor() {
    /** @type {SyncCoordinatorStatus} */
    this.snapshot = STOPPED_STATUS
    /** @type {Set<(snapshot: SyncCoordinatorStatus) => void>} */
    this.listeners = new Set()
    /** @type {{retry: () => Promise<void>} | null} */
    this.owner = null
  }

  /**
   * Activates the exact lifecycle owner allowed to retry.
   * @param {{retry: () => Promise<void>}} owner Current lifecycle owner.
   * @throws {Error} When the owner has no retry contract.
   */
  activate(owner) {
    if (!owner || typeof owner.retry !== "function") throw new Error("Project sync status owner requires retry")
    this.owner = owner
  }

  /**
   * Clears only the exact lifecycle owner.
   * @param {{retry: () => Promise<void>}} owner Candidate lifecycle owner.
   */
  deactivate(owner) {
    if (this.owner !== owner) return
    this.owner = null
    this.publish(STOPPED_STATUS)
  }

  /**
   * Publishes an immutable privacy-safe coordinator snapshot.
   * @param {SyncCoordinatorStatus} status Safe coordinator status.
   */
  publish(status) {
    this.snapshot = immutableStatus(status)
    for (const listener of this.listeners) listener(this.snapshot)
  }

  /**
   * Subscribes to the current and future snapshots.
   * @param {(snapshot: SyncCoordinatorStatus) => void} listener Observer.
   * @returns {() => void} Idempotent unsubscribe.
   */
  subscribe = (listener) => {
    this.listeners.add(listener)
    listener(this.snapshot)

    return () => { this.listeners.delete(listener) }
  }

  /**
   * Reads the stable React external-store snapshot.
   * @returns {SyncCoordinatorStatus} Current snapshot.
   */
  getSnapshot = () => this.snapshot

  /**
   * Runs one coordinator-owned manual retry.
   * @returns {Promise<void>} Retry completion.
   * @rejects {Error} When no lifecycle is active or retry fails.
   */
  async retry() {
    if (!this.owner) throw new Error("No active project sync lifecycle to retry")
    await this.owner.retry()
  }
}

/**
 * Creates an isolated store for application ownership or tests.
 * @returns {ProjectSyncStatusStore} Inactive status store.
 */
export function createProjectSyncStatusStore() {
  return new ProjectSyncStatusStore()
}

export const projectSyncStatusStore = createProjectSyncStatusStore()

/**
 * Subscribes React consumers to the app-owned status store.
 * @returns {SyncCoordinatorStatus} Current status snapshot.
 */
export function useProjectSyncStatus() {
  return useSyncExternalStore(projectSyncStatusStore.subscribe, projectSyncStatusStore.getSnapshot, projectSyncStatusStore.getSnapshot)
}

/**
 * Clones and freezes public status at the application boundary.
 * @param {SyncCoordinatorStatus} status Source status.
 * @returns {SyncCoordinatorStatus} Immutable application snapshot.
 */
function immutableStatus(status) {
  return Object.freeze({
    ...status,
    conflicts: Object.freeze(status.conflicts.map((conflict) => Object.freeze({...conflict}))),
    failure: status.failure ? Object.freeze({...status.failure}) : null
  })
}
